import { watch } from 'vue';
import { useBroadcastChannel } from '@vueuse/core';
import { LAST_ACTIVITY_TIMESTAMP_KEY } from '@/shared/common/constants/auth.constant';
import { BroadcastMessageType } from '@/shared/common/constants/sessionBroadcast.constant';
import {
  handleCloseOtherTabs,
  handleCloseAllTabs,
  handleTokenRefreshed,
  handleUserActivity,
  handleExpirationAlertShown,
  handleExpirationAlertHidden,
  getTabId,
} from '@/shared/common/utils/sessionBroadcast.util';
import type { BroadcastMessage, TokenData } from '@/shared/common/types/sessionBroadcast.type';

let isListenerInitialized = false;
let onUserActivityCallback: ((timestamp: number) => void) | null = null;
let onExpirationAlertShownCallback: ((timeRemaining: number) => void) | null = null;
let onExpirationAlertHiddenCallback: (() => void) | null = null;
let onLogoutCallback: (() => void) | null = null;

function handleBroadcastMessage(message: BroadcastMessage | null): void {
  if (!message) return;

  switch (message.type) {
    case BroadcastMessageType.CLOSE_OTHER_TABS:
      handleCloseOtherTabs(message, onLogoutCallback);
      break;
    case BroadcastMessageType.CLOSE_ALL_TABS:
      handleCloseAllTabs(onLogoutCallback);
      break;
    case BroadcastMessageType.TOKEN_REFRESHED:
      handleTokenRefreshed(message);
      break;
    case BroadcastMessageType.USER_ACTIVITY:
      handleUserActivity(message, onUserActivityCallback);
      break;
    case BroadcastMessageType.EXPIRATION_ALERT_SHOWN:
      handleExpirationAlertShown(message, onExpirationAlertShownCallback);
      break;
    case BroadcastMessageType.EXPIRATION_ALERT_HIDDEN:
      handleExpirationAlertHidden(message, onExpirationAlertHiddenCallback);
      break;
  }
}

/**
 * Composable para sincronizar el estado de sesión entre múltiples pestañas del navegador.
 *
 * Utiliza BroadcastChannel API para comunicar eventos de sesión (cierre de sesión, refresh de tokens,
 * actividad del usuario, alertas de expiración) entre todas las pestañas abiertas del mismo dominio.
 * Esto garantiza que todas las pestañas mantengan un estado de sesión consistente y evita que una
 * pestaña inactiva cierre la sesión si el usuario está activo en otra pestaña.
 *
 * @returns Objeto con funciones para notificar eventos y configurar callbacks:
 * - `notifySessionChange`: Notifica a otras pestañas que deben cerrarse
 * - `notifyLogOutAllTabs`: Notifica a todas las pestañas (incluida la actual) que deben cerrar sesión
 * - `notifyTokenRefreshed`: Notifica cuando se refresca el token para sincronizar en todas las pestañas
 * - `notifyUserActivity`: Notifica actividad del usuario para sincronizar timestamps de inactividad
 * - `notifyExpirationAlertShown`: Notifica cuando se muestra la alerta de expiración
 * - `notifyExpirationAlertHidden`: Notifica cuando se oculta la alerta de expiración
 * - `setOnUserActivityCallback`: Configura callback para recibir notificaciones de actividad de otras pestañas
 * - `setOnExpirationAlertShownCallback`: Configura callback para recibir notificaciones de alerta mostrada
 * - `setOnExpirationAlertHiddenCallback`: Configura callback para recibir notificaciones de alerta oculta
 * - `setOnLogoutCallback`: Configura callback para ejecutar cuando se cierra sesión desde otra pestaña
 */
export function useSessionBroadcast() {
  const tabId = getTabId();
  const { post, data } = useBroadcastChannel<
    BroadcastMessage,
    BroadcastMessage
  >({
    name: BroadcastMessageType.CHANNEL,
  });

  if (!isListenerInitialized) {
    watch(
      () => data.value,
      (message) => handleBroadcastMessage(message),
      { deep: true }
    );

    isListenerInitialized = true;
  }

  const notifySessionChange = () => {
    post({
      type: BroadcastMessageType.CLOSE_OTHER_TABS,
      senderId: tabId,
    });
  };

  const notifyLogOutAllTabs = () => {
    const message: BroadcastMessage = {
      type: BroadcastMessageType.CLOSE_ALL_TABS,
      senderId: tabId,
    };
    post(message);
    handleBroadcastMessage(message);
  };

  const notifyTokenRefreshed = (tokenData: TokenData) => {
    const message: BroadcastMessage = {
      type: BroadcastMessageType.TOKEN_REFRESHED,
      senderId: tabId,
      tokenData,
    };
    post(message);
  };

  const notifyUserActivity = (timestamp: number) => {
    localStorage.setItem(LAST_ACTIVITY_TIMESTAMP_KEY, timestamp.toString());
    const message: BroadcastMessage = {
      type: BroadcastMessageType.USER_ACTIVITY,
      senderId: tabId,
      activityTimestamp: timestamp,
    };
    post(message);
  };

  const notifyExpirationAlertShown = (timeRemaining: number) => {
    const message: BroadcastMessage = {
      type: BroadcastMessageType.EXPIRATION_ALERT_SHOWN,
      senderId: tabId,
      timeRemaining,
    };
    post(message);
  };

  const notifyExpirationAlertHidden = () => {
    const message: BroadcastMessage = {
      type: BroadcastMessageType.EXPIRATION_ALERT_HIDDEN,
      senderId: tabId,
    };
    post(message);
  };

  const setOnUserActivityCallback = (callback: (timestamp: number) => void) => {
    onUserActivityCallback = callback;
  };

  const setOnExpirationAlertShownCallback = (callback: (timeRemaining: number) => void) => {
    onExpirationAlertShownCallback = callback;
  };

  const setOnExpirationAlertHiddenCallback = (callback: () => void) => {
    onExpirationAlertHiddenCallback = callback;
  };

  const setOnLogoutCallback = (callback: () => void) => {
    onLogoutCallback = callback;
  };

  return {
    notifySessionChange,
    notifyLogOutAllTabs,
    notifyTokenRefreshed,
    notifyUserActivity,
    notifyExpirationAlertShown,
    notifyExpirationAlertHidden,
    setOnUserActivityCallback,
    setOnExpirationAlertShownCallback,
    setOnExpirationAlertHiddenCallback,
    setOnLogoutCallback,
  };
}
